/* TK Wishlist — localStorage CRUD + <wishlist-button> custom element */

const STORAGE_KEY = 'tk-wishlist';

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function isInWishlist(productId, variantId) {
  return getWishlist().some((item) => {
    if (variantId && item.variantId) {
      return String(item.variantId) === String(variantId);
    }
    return String(item.id || item) === String(productId);
  });
}

function findInWishlist(productId, variantId) {
  return getWishlist().find((item) => {
    if (variantId && item.variantId) {
      return String(item.variantId) === String(variantId);
    }
    return String(item.id || item) === String(productId);
  });
}

function toggleWishlist(productId, productData) {
  const id = String(productId);
  const variantId = productData && productData.variantId ? String(productData.variantId) : null;
  let list = getWishlist();
  const index = list.findIndex((item) => {
    if (variantId && item.variantId) {
      return String(item.variantId) === String(variantId);
    }
    return String(item.id || item) === id;
  });
  let added;

  if (index > -1) {
    list.splice(index, 1);
    added = false;
  } else {
    list.push(
      productData || {
        id,
        handle: '',
        title: '',
        image: '',
        price: '',
        url: '',
      }
    );
    added = true;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent('wishlist:change', { detail: { productId: id, variantId, list } }));
  updateHeaderCount(list.length);
  return added;
}

function updateHeaderCount(count) {
  document.querySelectorAll('.tk-wishlist-count').forEach(function(badge) {
    badge.textContent = count;
    badge.hidden = count === 0;
  });
  document.querySelectorAll('.tk-header__mob-icon--wishlist').forEach(function(el) {
    el.classList.toggle('has-items', count > 0);
  });
}

/* --- <wishlist-button> custom element --- */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId || null;
    this.updateState();
    this.addEventListener('click', this.handleClick.bind(this));
    this._onChange = this.handleChange.bind(this);
    window.addEventListener('wishlist:change', this._onChange);

    this._onVariantUpdate = this.handleVariantUpdate.bind(this);
    const scope = this.closest('.shopify-section, product-card');
    if (scope) {
      this._variantScope = scope;
      // 'variant:update' = Horizonův VariantUpdateEvent (PDP, variant picker).
      // 'tk:variant:update' = swap sourozeneckého swatche na kartě z
      // tk-product-card.js. Ten Horizonův event posílat nemůže, protože nemá
      // detail.data.html a shodil by product-card.js / product-form.js.
      scope.addEventListener('variant:update', this._onVariantUpdate);
      scope.addEventListener('tk:variant:update', this._onVariantUpdate);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('wishlist:change', this._onChange);
    if (this._variantScope) {
      this._variantScope.removeEventListener('variant:update', this._onVariantUpdate);
      this._variantScope.removeEventListener('tk:variant:update', this._onVariantUpdate);
      this._variantScope = null;
    }
  }

  handleVariantUpdate(e) {
    const resource = e.detail && e.detail.resource;
    if (!resource) return;

    this.variantId = String(resource.id);
    this.dataset.variantId = this.variantId;

    if (resource.price != null) {
      const formatted = (resource.price / 100).toLocaleString('cs-CZ') + '\u00a0Kč';
      this.dataset.price = formatted;
    }

    const variantTitle = resource.title && resource.title !== 'Default Title' ? resource.title : '';
    this.dataset.variantTitle = variantTitle;

    if (resource.url) {
      this.dataset.url = resource.url;
    } else {
      const baseUrl = this.dataset.url ? this.dataset.url.split('?')[0] : '';
      if (baseUrl) {
        this.dataset.url = baseUrl + '?variant=' + resource.id;
      }
    }

    // Update image from gallery slide after variant switch (metafield images aren't in the JSON resource)
    requestAnimationFrame(() => {
      const galleryImg = this.getGalleryImage();
      if (galleryImg) this.dataset.image = galleryImg;
    });

    this.updateState();
  }

  getGalleryImage() {
    // Collection card — first slide for the current variant (ignore hover state)
    const card = this.closest('product-card, .product-card');
    if (card) {
      let img = null;
      if (this.variantId) {
        img = card.querySelector('slideshow-slide[slide-id="variant-meta-' + this.variantId + '"] img');
      }
      if (!img) {
        img = card.querySelector('slideshow-slide:first-child img');
      }
      return img ? img.src : null;
    }
    // PDP — always first slide (variant's primary image, ignore user scroll position)
    const section = this.closest('.shopify-section');
    if (section) {
      const img = section.querySelector('media-gallery slideshow-slides slideshow-slide:first-child img');
      return img ? img.src : null;
    }
    return null;
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    let image = this.dataset.image || '';
    const galleryImg = this.getGalleryImage();
    if (galleryImg) image = galleryImg;

    const data = {
      id: String(this.productId),
      variantId: this.variantId ? String(this.variantId) : null,
      handle: this.dataset.handle || '',
      title: this.dataset.title || '',
      variantTitle: this.dataset.variantTitle || '',
      image,
      price: this.dataset.price || '',
      url: this.dataset.url || '',
    };

    const added = toggleWishlist(this.productId, data);
    this.classList.toggle('is-active', added);

    const svg = this.querySelector('svg');
    if (svg) {
      svg.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], {
        duration: 300,
        easing: 'ease-out',
      });
    }
  }

  handleChange(e) {
    const detail = e.detail;
    if (this.variantId && detail.variantId) {
      if (String(detail.variantId) === String(this.variantId)) {
        this.updateState();
      }
    } else if (String(detail.productId) === String(this.productId)) {
      this.updateState();
    }
  }

  updateState() {
    this.classList.toggle('is-active', isInWishlist(this.productId, this.variantId));
  }
}

customElements.define('wishlist-button', WishlistButton);

/* Header count is set by inline script in header.liquid for instant render (no flash).
   This handles dynamic updates after toggle. */

/* Expose for wishlist page */
window.MoWishlist = { getWishlist, isInWishlist, toggleWishlist, updateHeaderCount };

/* --- Wishlist PAGE renderer ---
   MORPH-PROOF: definováno globálně, connectedCallback se spustí i při morph
   navigaci (theme používá morph.js → inline scripty v sekci se NEspustí, takže
   render musí být v custom elementu). Fetchuje plné product-card karty přes
   /products/HANDLE?section_id=tk-card-html a injektuje je; globální observer
   v tk-product-card.js je pak enrichne (swatche/ceny/badge/ATC). */
class WishlistPage extends HTMLElement {
  connectedCallback() {
    if (this._tkInit) return;
    this._tkInit = true;
    this._cache = {};
    this._onChange = () => this.render();
    window.addEventListener('wishlist:change', this._onChange);
    this.render();
  }

  disconnectedCallback() {
    if (this._onChange) window.removeEventListener('wishlist:change', this._onChange);
  }

  get localePrefix() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root === '/' ? '' : root.replace(/\/+$/, '');
  }

  getList() {
    try { return (window.MoWishlist && window.MoWishlist.getWishlist()) || JSON.parse(localStorage.getItem('tk-wishlist') || '[]'); }
    catch (e) { return []; }
  }

  fetchCardHTML(handle) {
    if (this._cache[handle] !== undefined) return Promise.resolve(this._cache[handle]);
    const self = this;
    // TK: &country= aby se sekce vyrenderovala v aktivním marketu (jinak primární CZK).
    const country = (window.__tkCountry || (window.Shopify && window.Shopify.country) || '');
    return fetch(this.localePrefix + '/products/' + handle + '?section_id=tk-card-html' + (country ? '&country=' + country : ''))
      .then((r) => (r.ok ? r.text() : null))
      .then((html) => {
        let out = null;
        if (html) {
          const div = document.createElement('div');
          div.innerHTML = html;
          const card = div.querySelector('li.product-grid__item') || div.querySelector('product-card');
          out = card ? card.outerHTML : null;
        }
        self._cache[handle] = out;
        return out;
      })
      .catch(() => null);
  }

  render() {
    const grid = this;
    const loading = document.getElementById('wishlist-loading');
    const empty = document.getElementById('wishlist-empty');
    const list = this.getList();

    if (!list.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      if (loading) loading.hidden = true;
      return;
    }

    if (empty) empty.hidden = true;
    if (loading) loading.hidden = false;
    grid.innerHTML = '';

    const hide = () => { if (loading) loading.hidden = true; };
    const safety = setTimeout(hide, 10000); // spinner se nikdy „netočí donekonečna"
    const self = this;

    Promise.all(list.map((item) => (item.handle ? self.fetchCardHTML(item.handle) : Promise.resolve(null))))
      .then((cards) => { grid.innerHTML = cards.filter(Boolean).join(''); })
      .catch(() => {})
      .then(() => { clearTimeout(safety); hide(); });
  }
}

if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
